import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import '../components/ToastContainer/ToastContainer.css';

const ToastContext = createContext(null);

const DEFAULT_DURATION_MS = 4000;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timeoutsRef = useRef(new Map());

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const push = useCallback((type, content, options = {}) => {
    const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const duration = options.duration;
    const resolvedDuration = duration === null ? null : (typeof duration === 'number' ? duration : DEFAULT_DURATION_MS);

    setToasts((prev) => {
      const next = [{ id, type, content }, ...prev];
      return next.slice(0, 5);
    });

    if (resolvedDuration !== null) {
      const timeout = setTimeout(() => remove(id), resolvedDuration);
      timeoutsRef.current.set(id, timeout);
    }

    return id;
  }, [remove]);

  const api = useMemo(() => ({
    info: (msg, opts) => push('info', msg, opts),
    success: (msg, opts) => push('success', msg, opts),
    error: (msg, opts) => push('error', msg, opts),
    custom: (type, content, opts) => push(type, content, opts),
    remove,
  }), [push, remove]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-container" aria-live="polite" aria-relevant="additions removals">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`} role="status">
            <div className="toast-message">{t.content}</div>
            <button type="button" className="toast-close" onClick={() => remove(t.id)} aria-label="Close">
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};

export default ToastContext;
